import React from "react";
import { Card, CardActions, Typography, Table, TableCell, TableBody, TableRow, CardHeader, CardContent, Chip, Collapse, Grid } from "@material-ui/core";
import styled from "styled-components";
import ReactMarkdown from "react-markdown";
import Theme from "../../Theme";
import ISupportCall from "../../models/ISupportCall";
import LinkButton from "../LinkButton";
import { getArea } from "../../functions/serviceCallFunctions";
import ServiceCallNoteGrid from "./ServiceCallNoteGrid";
import AttachmentForm from "../forms/AttachmentForm";
import { SecondaryFormButton } from "./FormButton";
import moment from "moment";
import StyledMarkdown from "../StyledMarkdown";
import { AttachmentTypes } from "../../functions/getAttachmentType";

const StyledCard = styled(Card)`
  max-width: 1200px;
`;

const StyledCardActions = styled(CardActions)`
  padding-left: 16px;
  background-color: ${Theme.colors.buttonSecondary};
  color: ${Theme.colors.white};
`;

const StyledTypography = styled(Typography)`
  margin-left: auto;
  padding-right: 16px;
  font-weight: 500;
`;

const StyledTable = styled(Table)`
  max-width: 1200px;
`;

const StyledPaperTableCell = styled(TableCell)`
  border-bottom: 0px;
  max-width: 1200px;
`;

const StyledChip = styled(Chip)`
  background-color: ${Theme.colors.white};
`;

enum FormStateType {
  closed = "closed",
  addNote = "addNote",
  addAttachment = "addAttachment",
}

interface ITicketProps {
  ticket: ISupportCall;
  token: string;
  updateNotes: Function;
}

export default function SCDetailTicketInfo(props: ITicketProps) {
  const {
    ticket: { id, description, status },
  } = props;

  const [formState, setFormState] = React.useState<FormStateType>(FormStateType.closed);

  const getHeaderData = (ticketNum: string, ticketTitle: string) => {
    return `Support Ticket ${ticketNum} - ${ticketTitle}`;
  };

  const handleFormToggle = (type: FormStateType) => () => {
    setFormState(formState === type ? FormStateType.closed : type);
  };

  const getNewNoteGrid = () => {
    return (
      <ServiceCallNoteGrid
        token={props.token}
        serviceCallNumber={id.toString()}
        updateNotes={props.updateNotes}
        handleShowNewNoteFields={handleFormToggle(FormStateType.addNote)}
      />
    );
  };

  const isOpen = !status || status === "Open";

  const getAttachmentForm = () => (
    <AttachmentForm
      rmsObjectId={id}
      rmsObjectIsOpen={isOpen}
      onClose={handleFormToggle(FormStateType.addAttachment)}
      attachmentParentType={AttachmentTypes.ServiceCall}
      isInstantUpload={true}
      rmsObjectClosedMessage="Support ticket is closed"
    />
  );

  const getForm = () => {
    switch (formState) {
      case FormStateType.addAttachment:
        return getAttachmentForm();
      case FormStateType.addNote:
        return getNewNoteGrid();
      default:
        return null;
    }
  };

  return (
    <StyledTable>
      <TableBody>
        <TableRow>
          <StyledPaperTableCell>
            <StyledCard elevation={2}>
              <CardHeader
                title={getHeaderData(props.ticket.id, props.ticket.summary)}
                action={
                  <>
                    <SecondaryFormButton onClick={handleFormToggle(FormStateType.addNote)} variant="contained" disabled={formState === FormStateType.addNote}>
                      Add Note
                    </SecondaryFormButton>
                    <SecondaryFormButton
                      onClick={handleFormToggle(FormStateType.addAttachment)}
                      variant="contained"
                      disabled={formState === FormStateType.addAttachment}
                    >
                      Attachments
                    </SecondaryFormButton>
                    <LinkButton to="/list-sc">Return To List</LinkButton>
                  </>
                }
              />
              <CardContent>
                <Typography variant="body1" component="div">
                  <StyledMarkdown content={<ReactMarkdown children={description} />} />
                </Typography>
              </CardContent>
              <StyledCardActions>
                <StyledChip size="small" label={props.ticket.product} />
                <StyledChip size="small" label={props.ticket.productType} />
                <StyledChip size="small" label={getArea(props.ticket)} />
                <StyledTypography variant="caption">{moment(props.ticket.dateSubmitted).fromNow()}</StyledTypography>
              </StyledCardActions>
              <Collapse in={formState !== FormStateType.closed} timeout="auto" unmountOnExit>
                <CardContent>
                  <Grid container direction="column" spacing={1}>
                    {getForm()}
                  </Grid>
                </CardContent>
              </Collapse>
            </StyledCard>
          </StyledPaperTableCell>
        </TableRow>
      </TableBody>
    </StyledTable>
  );
}
